/**
 * A generic modal choice list.
 *
 * Several actions need a second decision that is not a board target — which tech, which
 * trade, which megastructure. Rather than three bespoke sub-screens, they all funnel into
 * one list: same navigation, same buttons, same look. One thing for players to learn.
 */

import { INK, seatColor, TYPE, withAlpha } from '../render/palette';
import { hintBar, panel, paragraph, roundRect, text } from '../render/widgets';
import type { GameAction, PlayerId } from '../game/types';

export interface ChoiceItem {
  label: string;
  detail: string;
  /** Right-aligned cost or value badge. */
  badge?: string;
  badgeColor?: string;
  /** The action committed if this row is picked. */
  action: GameAction;
  enabled: boolean;
  /** Why it is disabled, shown when highlighted. */
  reason?: string;
}

export interface ChoiceState {
  title: string;
  seat: PlayerId;
  items: ChoiceItem[];
  index: number;
}

/** Move the highlight, skipping nothing — disabled rows stay visitable so the reason shows. */
export function moveChoice(choice: ChoiceState, delta: number): void {
  if (choice.items.length === 0) return;
  const count = choice.items.length;
  choice.index = ((choice.index + delta) % count + count) % count;
}

export function selectedChoice(choice: ChoiceState): ChoiceItem | null {
  return choice.items[choice.index] ?? null;
}

export function drawChoice(
  ctx: CanvasRenderingContext2D,
  choice: ChoiceState,
  width: number,
  height: number,
): void {
  const color = seatColor(choice.seat);

  ctx.save();
  ctx.fillStyle = 'rgba(4, 5, 10, 0.7)';
  ctx.fillRect(0, 0, width, height);
  ctx.restore();

  const rowH = 44;
  const menuW = Math.min(560, width * 0.6);
  // Cap the visible rows so a 12-item trade list still fits on a small window.
  const maxRows = Math.max(4, Math.floor((height * 0.62) / rowH));
  const visible = Math.min(choice.items.length, maxRows);
  const menuH = visible * rowH + 104;
  const x = width / 2 - menuW / 2;
  const y = height / 2 - menuH / 2;

  panel(ctx, x, y, menuW, menuH, {
    fill: 'rgba(10, 12, 22, 0.97)',
    border: withAlpha(color, 0.85),
    borderWidth: 2.4,
    accent: color,
    accentWidth: 5,
    glow: withAlpha(color, 0.35),
    chamfer: 20,
  });

  text(ctx, choice.title, x + 22, y + 30, {
    size: TYPE.body, weight: 800, color, baseline: 'middle', maxWidth: menuW - 44,
  });

  // Scroll window so the highlighted row is always on screen.
  const half = Math.floor(visible / 2);
  let start = choice.index - half;
  start = Math.max(0, Math.min(start, choice.items.length - visible));

  for (let row = 0; row < visible; row++) {
    const itemIndex = start + row;
    const item = choice.items[itemIndex];
    if (!item) break;

    const rowY = y + 52 + row * rowH;
    const selected = itemIndex === choice.index;

    if (selected) {
      roundRect(ctx, x + 10, rowY, menuW - 20, rowH - 5, 9);
      ctx.fillStyle = withAlpha(color, item.enabled ? 0.26 : 0.1);
      ctx.fill();
      ctx.strokeStyle = withAlpha(color, item.enabled ? 0.9 : 0.4);
      ctx.lineWidth = 1.6;
      ctx.stroke();
    }

    ctx.save();
    if (!item.enabled) ctx.globalAlpha = 0.4;

    text(ctx, item.label, x + 26, rowY + rowH / 2 - 8, {
      size: TYPE.label, weight: 800, color: INK.text, baseline: 'middle',
      maxWidth: menuW - 150,
    });
    text(ctx, item.detail, x + 26, rowY + rowH / 2 + 9, {
      size: TYPE.micro, weight: 500, color: INK.textDim, baseline: 'middle',
      maxWidth: menuW - 150,
    });

    if (item.badge) {
      text(ctx, item.badge, x + menuW - 24, rowY + rowH / 2 - 2, {
        size: TYPE.label, weight: 800, color: item.badgeColor ?? INK.info,
        align: 'right', baseline: 'middle',
      });
    }
    ctx.restore();
  }

  // Scroll affordances, so a truncated list does not look like the whole list.
  if (start > 0) {
    text(ctx, '▲', x + menuW / 2, y + 48, {
      size: TYPE.micro, color: INK.textFaint, align: 'center', baseline: 'middle',
    });
  }
  if (start + visible < choice.items.length) {
    text(ctx, '▼', x + menuW / 2, y + 52 + visible * rowH + 4, {
      size: TYPE.micro, color: INK.textFaint, align: 'center', baseline: 'middle',
    });
  }

  // Reason the highlighted row cannot be taken.
  const current = selectedChoice(choice);
  if (current && !current.enabled && current.reason) {
    paragraph(ctx, current.reason, x + 22, y + menuH - 40, menuW - 44, {
      size: TYPE.micro, weight: 600, color: INK.bad, lineHeight: 15,
    });
  }

  hintBar(
    ctx,
    [{ button: 'a', label: 'Choose' }, { button: 'b', label: 'Back' }],
    x + 22, y + menuH - 18,
  );
}
